import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Image,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spacer,
  Text,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { LuSlidersHorizontal } from "react-icons/lu";
import { useActiveAccount } from "applesauce-react/hooks";
import { NostrEvent, kinds } from "nostr-tools";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getNutzapAmount, NUTZAP_KIND } from "applesauce-wallet/helpers";
import { getZapPayment } from "applesauce-common/helpers";

import NoteFilterTypeButtons from "../../components/note-filter-type-buttons";
import FeedSortTabs, { FeedSortMode } from "../../components/feed-sort-tabs";
import RouterLink from "../../components/router-link";
import TimelinePage, { useTimelinePageEventFilter } from "../../components/timeline-page";
import { GENERIC_TIMELINE_KINDS } from "../../components/timeline-page/generic-note-timeline";
import TimelineViewTypeButtons from "../../components/timeline-page/timeline-view-type";
import VerticalPageLayout from "../../components/vertical-page-layout";
import { useMobileHeaderActions } from "../../components/layout/mobile/mobile-header-context";
import { isReply, isRepost } from "../../helpers/nostr/event";
import useClientSideMuteFilter from "../../hooks/use-client-side-mute-filter";
import useLocalStorageDisclosure from "../../hooks/use-localstorage-disclosure";
import useRouteSearchValue from "../../hooks/use-route-search-value";
import useTimelineLoader from "../../hooks/use-timeline-loader";
import useUserContacts from "../../hooks/use-user-contacts";
import { LOCAL_RELAY_URL } from "../../const";
import { eventStore } from "../../services/event-store";
import { nostrRequest } from "../../services/pool";

function useHotSort(timeline: NostrEvent[], sortMode: FeedSortMode) {
  const [version, setVersion] = useState(0);

  // Prefetch nutzaps for visible events
  useEffect(() => {
    if (sortMode !== "hot" || timeline.length === 0) return;
    const eventIds = timeline.map((e) => e.id);
    const sub = nostrRequest([LOCAL_RELAY_URL], [{ kinds: [NUTZAP_KIND as number], "#e": eventIds }]).subscribe({
      next: (event) => eventStore.add(event),
    });
    return () => sub.unsubscribe();
  }, [sortMode, timeline.length]);

  // Re-sort when new nutzaps arrive
  useEffect(() => {
    if (sortMode !== "hot") return;
    const sub = eventStore.insert$.subscribe((event) => {
      if (event.kind === (NUTZAP_KIND as number) || event.kind === kinds.Zap) {
        setVersion((v) => v + 1);
      }
    });
    return () => sub.unsubscribe();
  }, [sortMode]);

  return useMemo(() => {
    if (sortMode !== "hot") return timeline;

    const scored = timeline.map((event) => {
      const zaps = eventStore.getByFilters({ kinds: [kinds.Zap], "#e": [event.id] });
      const nutzaps = eventStore.getByFilters({ kinds: [NUTZAP_KIND as number], "#e": [event.id] });
      const zapTotal = zaps.reduce((sum, z) => sum + (getZapPayment(z)?.amount ?? 0), 0);
      const nutzapTotal = nutzaps.reduce((sum, n) => sum + (getNutzapAmount(n) ?? 0), 0) * 1000;
      return { event, score: zapTotal + nutzapTotal };
    });

    return scored
      .sort((a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        return b.event.created_at - a.event.created_at;
      })
      .map((s) => s.event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortMode, timeline, version]);
}

function HomePage() {
  const account = useActiveAccount();
  const showReplies = useLocalStorageDisclosure("show-replies", false);
  const showReposts = useLocalStorageDisclosure("show-reposts", true);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const sortParam = useRouteSearchValue("sort", "recent");
  const sortMode = sortParam.value as FeedSortMode;

  const contacts = useUserContacts(account?.pubkey);
  const contactPubkeys = useMemo(() => {
    if (!contacts) return new Set<string>();
    return new Set(contacts.map((c) => c.pubkey));
  }, [contacts]);

  const timelinePageEventFilter = useTimelinePageEventFilter();
  const muteFilter = useClientSideMuteFilter();
  const eventFilter = useCallback(
    (event: NostrEvent) => {
      if (muteFilter(event)) return false;
      if (!showReplies.isOpen && isReply(event)) return false;
      if (!showReposts.isOpen && isRepost(event)) return false;
      if (sortMode === "following" && !contactPubkeys.has(event.pubkey)) return false;
      return timelinePageEventFilter(event);
    },
    [timelinePageEventFilter, showReplies.isOpen, showReposts.isOpen, muteFilter, sortMode, contactPubkeys],
  );

  const relays = useMemo(() => [LOCAL_RELAY_URL], []);

  const { loader, timeline } = useTimelineLoader(
    "community-feed",
    relays,
    { kinds: GENERIC_TIMELINE_KINDS },
    { eventFilter },
  );

  const sortedTimeline = useHotSort(timeline, sortMode);

  const headerActions = useMemo(
    () => (
      <Popover placement="bottom-end" isLazy>
        <PopoverTrigger>
          <IconButton aria-label="Feed options" icon={<LuSlidersHorizontal />} variant="ghost" size="sm" />
        </PopoverTrigger>
        <PopoverContent w="auto" minW="200px">
          <PopoverBody>
            <VStack align="stretch" spacing="3">
              <Box>
                <Text fontSize="xs" color="gray.500" mb="1">Sort</Text>
                <FeedSortTabs size="sm" />
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500" mb="1">Filter</Text>
                <NoteFilterTypeButtons showReplies={showReplies} showReposts={showReposts} size="sm" />
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500" mb="1">View</Text>
                <TimelineViewTypeButtons size="sm" />
              </Box>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    ),
    [showReplies.isOpen, showReposts.isOpen],
  );

  useMobileHeaderActions("Feed", headerActions);

  const header = isMobile ? undefined : (
    <Flex gap="2" alignItems="center">
      <Text fontWeight="bold" fontSize="lg">
        Feed
      </Text>
      <Spacer />
      <FeedSortTabs />
      <NoteFilterTypeButtons showReplies={showReplies} showReposts={showReposts} />
      <TimelineViewTypeButtons />
    </Flex>
  );

  return <TimelinePage loader={loader} timeline={sortedTimeline} header={header} sortMode={sortMode} pt="2" pb="12" px="2" maxW="6xl" />;
}

export default function HomeView() {
  const account = useActiveAccount();

  if (!account)
    return (
      <VerticalPageLayout alignItems="center" justifyContent="center" minH="full">
        <Box textAlign="center">
          <Image src="/filler/litstrudel.png" alt="litStrudel" h="220px" objectFit="contain" mx="auto" mb={4} />
          <Heading size="lg" pb={4} fontFamily="'Press Start 2P', cursive" fontSize="xl" lineHeight="1.6">
            Welcome to litStrudel
          </Heading>
          <Text my={4}>A Litecoin Lightning community on Nostr.</Text>
          <Text fontSize="sm" color="gray.500" maxW="md" mx="auto" mb={6}>
            A forked noStrudel running on a decentralized relay, built for ultra-micropayments, community, and
            on-chain gaming. First of its kind. Currently in testing — things may catch fire (like this strudel).
          </Text>
          <Flex direction="column" gap="3" alignItems="center">
            <Button as={RouterLink} to="/signin" size="lg" minW="xs" colorScheme="primary">
              Sign in
            </Button>
            <Button as={RouterLink} to="/join" size="lg" minW="xs" variant="outline" colorScheme="green">
              Create Account
            </Button>
          </Flex>
        </Box>
      </VerticalPageLayout>
    );

  return <HomePage />;
}
